import { Box, Cell, Layout, Text } from '@wix/design-system';
import { EclipseCta } from '@wix/package-picker-shared-eclipse';
import React from 'react';
import { EclipsePackagePickerModule } from '../../modules/eclipse-package-picker.module';
import s from './EnterpriseBanner.scss';
import { EnterpriseBannerFeatureItem } from './EnterpriseBannerFeatureItem';

const buildEnterpriseUrlLink = ({
  localeUrl,
  referrer,
  locale,
  userGuid = 'null',
  clientId = 'null',
}: {
  localeUrl: string;
  referrer: string;
  locale: string;
  userGuid?: string;
  clientId?: string;
}): string => {
  const urlObj = new URL(localeUrl);
  urlObj.searchParams.append(
    'referrer',
    `${referrer}-${encodeURIComponent(locale)}`,
  );
  urlObj.searchParams.append('uuid', encodeURIComponent(userGuid));
  urlObj.searchParams.append('clientid', encodeURIComponent(clientId));

  return urlObj.toString();
};

/**
 * Displays an enterprise/custom solution CTA banner.
 *
 * **Context Dependencies** — This component has no props.
 * It reads the following from `EclipsePackagePickerModule.Provider` context:
 * - `locale` — used to build the enterprise contact URL
 * - `userId` — appended as `uuid` query param
 * - `clientId` — appended as `clientid` query param
 * - Translations — for banner title, subtitle, feature items, and CTA text
 * - BI events — fires `onEnterpriseBannerCtaClick`
 */
export const EnterpriseBanner = () => {
  const { t } = EclipsePackagePickerModule.internals.useTranslations();
  const { locale, userId, clientId } =
    EclipsePackagePickerModule.internals.useContext();
  const ENTERPRISE_REFERRER = 'wix-pp-main';

  const handleCTAOnClick = () => {
    const localeUrl = 'https://www.wix.com/enterprise/form';
    const enterpriseUrlLink = buildEnterpriseUrlLink({
      localeUrl,
      referrer: ENTERPRISE_REFERRER,
      locale,
      userGuid: userId,
      clientId,
    });
    EclipsePackagePickerModule.internals.bi.onEnterpriseBannerCtaClick?.({
      targetUrl: enterpriseUrlLink,
    });
    window.open(enterpriseUrlLink, '_blank');
  };

  return (
    <Box gap="90px" align="space-between" width="100%" maxWidth="1320px">
      <Box
        gap="6px"
        direction="vertical"
        align="space-between"
        width="100%"
        maxWidth="366px"
      >
        <Box direction="vertical" gap="SP2">
          <Text className={s.title}>
            {t('premium-purchase-plan.enterprise_banner.title')}
          </Text>
          <Text size="medium" weight="normal" className={s.subtitle}>
            {t('premium-purchase-plan.enterprise_banner.subtitle')}
          </Text>
        </Box>
        <Box marginTop="SP3">
          <EclipseCta onClick={handleCTAOnClick} variant="dark">
            {t('premium-purchase-plan.enterprise_banner.CTA')}
          </EclipseCta>
        </Box>
      </Box>
      <Box maxWidth="874px">
        <Layout gap="36px">
          <>
            <Cell span={6}>
              <EnterpriseBannerFeatureItem
                title="premium-purchase-plan.enterprise_banner.content_deployment_title"
                body="premium-purchase-plan.enterprise_banner.content_deployment_subtitle"
              />
            </Cell>

            <Cell span={6}>
              <EnterpriseBannerFeatureItem
                title="premium-purchase-plan.enterprise_banner.multi-site_management.title"
                body="premium-purchase-plan.enterprise_banner.multi-site_management.subtitle"
              />
            </Cell>

            <Cell span={6}>
              <EnterpriseBannerFeatureItem
                title="premium-purchase-plan.enterprise_banner.custom_integrations.title"
                body="premium-purchase-plan.enterprise_banner.custom_integrations.subtitle"
              />
            </Cell>

            <Cell span={6}>
              <EnterpriseBannerFeatureItem
                title="premium-purchase-plan.enterprise_banner.dedicated_account_support.title"
                body="premium-purchase-plan.enterprise_banner.dedicated_account_support.subtitle"
              />
            </Cell>
          </>
        </Layout>
      </Box>
    </Box>
  );
};
